import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import io from 'socket.io-client';
import { report } from '../utils/telemetry';

const SocketContext = createContext();

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [sensorData, setSensorData] = useState({});
  const [roomData, setRoomData] = useState({});
  const [relayData, setRelayData] = useState({});
  const [latestCameraImage, setLatestCameraImage] = useState(null);
  // Track last update timestamps so components know when data changed
  const [sensorUpdateTS, setSensorUpdateTS] = useState(0);
  const [roomUpdateTS, setRoomUpdateTS] = useState(0);
  // Meter pushes are a change marker only, not stored data: the Power page
  // refetches /meter/latest so that totals, power factor and staleness keep
  // coming from the single server-side implementation rather than being
  // recalculated here from raw phase rows.
  const [meterUpdateTS, setMeterUpdateTS] = useState(0);
  // Issues page: which issue changed and when, so an open list / issue refetches.
  const [issueUpdate, setIssueUpdate] = useState(null);
  // Purchases page: a request changed, so an open list refetches.
  const [purchaseUpdate, setPurchaseUpdate] = useState(null);

  useEffect(() => {
    // Connect to Socket.IO server
    // Use REACT_APP_API_URL if set, otherwise use current origin in production
    const socketUrl = process.env.REACT_APP_API_URL
      || (process.env.NODE_ENV === 'production' ? window.location.origin : window.location.origin);

    const newSocket = io(socketUrl, {
      transports: ['websocket', 'polling'],
      path: '/socket.io/'
    });

    // How long live data was gone. A phone drops the socket while the app is in the
    // background; `hidden` lets the summary keep those apart from real drops.
    let droppedAt = null; let dropReason = null; let droppedHidden = false;
    newSocket.on('connect', () => {
      console.log('Connected to server');
      setConnected(true);
      if (droppedAt) {
        report('socket_drop', { ms: performance.now() - droppedAt,
          details: { reason: dropReason, hidden: droppedHidden || document.hidden } });
        droppedAt = null;
      }
    });

    newSocket.on('disconnect', (reason) => {
      console.log('Disconnected from server');
      setConnected(false);
      if (reason === 'io client disconnect') return;      // our own close (sign-out, unmount)
      droppedAt = performance.now(); dropReason = reason; droppedHidden = document.hidden;
    });

    newSocket.on('sensor_update', (data) => {
      // Store full sensor payload keyed by unit_id
      setSensorData(prev => ({
        ...prev,
        [data.unit_id]: {
          unit_id: data.unit_id,
          timestamp: data.timestamp,
          reservoir: data.reservoir,
          climate: data.climate
        }
      }));
      setSensorUpdateTS(Date.now());
    });

    newSocket.on('room_update', (data) => {
      // Store full room sensor payload keyed by unit_id
      setRoomData(prev => ({
        ...prev,
        [data.unit_id]: data
      }));
      setRoomUpdateTS(Date.now());
    });

    newSocket.on('relay_update', (data) => {
      setRelayData(prev => ({
        ...prev,
        [data.unit_id]: data
      }));
    });

    newSocket.on('meter_update', () => {
      setMeterUpdateTS(Date.now());
    });

    newSocket.on('issue_updated', (data) => {
      setIssueUpdate({ id: data.id, ts: Date.now() });
    });

    newSocket.on('purchase_updated', (data) => {
      setPurchaseUpdate({ id: data.id, ts: Date.now() });
    });

    newSocket.on('connected', (data) => {
      console.log('Server message:', data);
    });

    newSocket.on('camera_image_uploaded', (data) => {
      setLatestCameraImage(data);
    });

    setSocket(newSocket);

    return () => {
      newSocket.close();
    };
  }, []);

  const joinUnit = useCallback((unitId) => {
    if (socket) {
      socket.emit('join_unit', { unit_id: unitId });
    }
  }, [socket]);

  const leaveUnit = useCallback((unitId) => {
    if (socket) {
      socket.emit('leave_unit', { unit_id: unitId });
    }
  }, [socket]);

  const value = {
    socket,
    connected,
    sensorData,
    roomData,
    relayData,
    latestCameraImage,
    sensorUpdateTS,
    roomUpdateTS,
    meterUpdateTS,
    issueUpdate,
    purchaseUpdate,
    joinUnit,
    leaveUnit
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
};